import { Clock } from "lucide-react"

export const AptitudeTest = ({ candidate }: { candidate: any }) => {
    const aptitude = candidate.aptitude

    return (
        <div className="rounded-md bg-white p-8 shadow-sm"> 
            <h2 className="mb-1 text-xl font-bold text-gray-900">Personality test</h2>
            <hr className="mb-7 w-1/4 h-[3px] bg-gray-200" />
            <div className="grid gap-12 lg:grid-cols-2">
                {/* Left Column: Overall Score & Chart */}
                <div>
                    <div className="mb-6 flex items-start justify-between">
                        <div>
                            <p className="text-sm font-bold text-black">Overall score</p>
                            <h3 className="text-3xl font-extrabold text-gray-900">
                                {aptitude.percentile}
                                <span className="text-xl">th</span> percentile
                            </h3>
                            <p className="mt-2 text-xs text-gray-500">
                                {candidate.name} scored <span className="font-bold">higher than {aptitude.percentile}%</span> of all
                                TestGorilla candidates.
                            </p>
                        </div>
                        <div className="text-right">
                            <button className="text-xs font-bold text-[#b234aa] hover:underline">How to interpret results</button>
                            <p className="mt-1 text-lg font-bold text-gray-900">{aptitude.rawScore}% raw score</p>
                        </div>
                    </div>

                    {/* Bell Curve Chart */}
                    <div className="relative mt-8 h-64 w-full">
                        <svg className="h-full w-full" viewBox="0 0 400 200" preserveAspectRatio="none">
                            {/* Grid Lines and Labels */}
                            <line x1="0" y1="180" x2="400" y2="180" stroke="#E5E7EB" strokeWidth="1" />
                            {[1, 20, 40, 60, 80, 99].map((val) => (
                                <text key={val} x={(val / 100) * 400} y="195" textAnchor="middle" className="text-[10px] fill-gray-400">
                                    {val}
                                </text>
                            ))}

                            {/* Regions Backgrounds */}
                            <rect x="0" y="0" width="80" height="180" fill="#F9FAFB" />
                            <rect x="80" y="0" width="80" height="180" fill="white" />
                            <rect x="160" y="0" width="80" height="180" fill="#F9FAFB" />
                            <rect x="240" y="0" width="80" height="180" fill="white" />
                            <rect x="320" y="0" width="80" height="180" fill="#FFF7ED" />

                            {/* Region Labels */}
                            <text x="40" y="170" textAnchor="middle" className="text-[10px] fill-gray-400">
                                Well below
                            </text>
                            <text x="120" y="170" textAnchor="middle" className="text-[10px] fill-gray-400">
                                Below
                            </text>
                            <text x="200" y="170" textAnchor="middle" className="text-[10px] fill-gray-400">
                                Average
                            </text>
                            <text x="280" y="170" textAnchor="middle" className="text-[10px] fill-gray-400">
                                Above
                            </text>
                            <text x="360" y="170" textAnchor="middle" className="text-[10px] fill-orange-400 font-medium">
                                Well above
                            </text>

                            {/* Bell Curve Path */}
                            <path d="M 0 180 Q 100 180 200 50 Q 300 180 400 180" fill="none" stroke="#374151" strokeWidth="2" />

                            {/* Candidate Score Line */}
                            <line
                                x1={(aptitude.percentile / 100) * 400}
                                y1="30"
                                x2={(aptitude.percentile / 100) * 400}
                                y2="180"
                                stroke="#374151"
                                strokeWidth="1"
                                strokeDasharray="4 2"
                            />
                            <circle
                                cx={(aptitude.percentile / 100) * 400}
                                cy="30"
                                r="12"
                                fill="white"
                                stroke="#374151"
                                strokeWidth="1"
                            />
                            <text
                                x={(aptitude.percentile / 100) * 400}
                                y="34"
                                textAnchor="middle"
                                className="text-[10px] font-bold fill-gray-900"
                            >
                                {aptitude.percentile}
                            </text>
                        </svg>

                        <div className="mt-4 flex items-center justify-center gap-6 text-[10px]">
                            <div className="flex items-center gap-2 text-gray-500">
                                <span className="h-2 w-2 rounded-md border border-gray-400"></span>
                                Candidate score
                            </div>
                            <div className="flex items-center gap-2 text-gray-500">
                                <span className="h-4 w-1 bg-gray-900"></span>
                                Highest score in your assessment
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Detailed Aptitude List */}
                <div className="space-y-4">
                    {aptitude.tests.map((test: any, i: number) => (
                        <div key={i} className="flex items-center justify-between rounded-md border border-gray-100 p-5 shadow-sm">
                            <div className="flex items-center gap-4">
                                <div className="pl-9">
                                    <h4 className="font-bold text-gray-900">{test.name}</h4>
                                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                                        <Clock className="h-3.5 w-3.5" />
                                        {test.time}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-8">
                                <div className="text-right">
                                    <p className="text-sm font-bold text-gray-900">{test.percentile} percentile</p>
                                    <span className="inline-block rounded-md px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#F3F4F6] text-gray-500">
                                        {test.status}
                                    </span>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-bold text-gray-900">{test.raw}</p>
                                    <p className="text-[10px] font-medium text-gray-400">Raw score</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
